import {useNavigate} from "react-router-dom";
import {VerifyService} from "../../service/VerifyService";
import {VerificationStepType} from "../../domain/verification/VerificationStepType";
import React, {useCallback, useState} from "react";
import ProcessingComponent from "../../components/common/processing/ProcessingComponent";

export const useStepCheck = (verificationId: string | undefined) => {
    const navigate = useNavigate()

    const [loading, setLoading] = useState(true)

    const evaluateUrl = useCallback(async (isApp?: boolean) => {
        setLoading(true)
        if (!verificationId) {
            navigate(`../error`, { replace: true })
        }
        const currentStep = await VerifyService.getInstance().getCurrentStep(verificationId!!)
        localStorage.setItem("next-step", currentStep.token)
        const redirectUrl = evaluateStepNextUrl(currentStep.step, isApp)
        const currentPath = window.location.pathname
        if (evaluateNeedToRedirect(currentPath, currentStep.step)) {
            navigate(`../${verificationId}/${redirectUrl}`, { replace: true })
        }
        setLoading(false)
    }, [navigate, verificationId])

    const evaluateNeedToRedirect = (currentPath: string, currentStep: VerificationStepType) => {
        if (!currentStep) {
            return !currentPath.includes("start") && !currentPath.includes("document")
        }

        switch (currentStep.toLowerCase()) {
            case VerificationStepType.DOCUMENT_CHOICE:
                return !currentStep.includes("consent")
            case VerificationStepType.CONSENT:
                return !currentPath.includes("front")
            case VerificationStepType.DOC_FRONT:
                return !currentPath.includes("back")
            case VerificationStepType.DOC_BACK:
                return !currentPath.includes("selfie")
            case VerificationStepType.DOC_NFC:
                return !currentPath.includes("selfie")
            case VerificationStepType.SELFIE:
                return !currentPath.includes("information")
            case VerificationStepType.INFORMATION:
            case VerificationStepType.IDV_VERIFICATION:
                return !currentPath.includes("complete")
            default:
                return true
        }
    }

    const redirectToStep = (step: VerificationStepType) => {
        let path = ""
        switch (step.toLowerCase()) {
            case VerificationStepType.DOCUMENT_CHOICE:
                path = "document"
                break
            case VerificationStepType.CONSENT:
                path = "consent"
                break
            case VerificationStepType.DOC_FRONT:
                path = "front"
                break
            case VerificationStepType.DOC_BACK:
                path = "back"
                break
            case VerificationStepType.DOC_NFC:
            case VerificationStepType.SELFIE:
            case VerificationStepType.LIVENESS:
                path = "take-selfie"
                break
            case VerificationStepType.INFORMATION:
                path = "information"
                break
            case VerificationStepType.IDV_VERIFICATION:
                path = "complete"
                break
            default:
                path = "document"
        }
        navigate(`../${verificationId}/${path}`, { replace: true })
    }

    const evaluateStepNextUrl = (currentStep: VerificationStepType, isApp?: boolean) => {
        if (!currentStep) {
            if (isApp) {
                return "document"
            }
            return "start"
        }
        switch (currentStep.toLowerCase()) {
            case VerificationStepType.DOCUMENT_CHOICE:
                return "consent"
            case VerificationStepType.CONSENT:
                return "front"
            case VerificationStepType.DOC_FRONT:
                return "back"
            case VerificationStepType.DOC_BACK:
            case VerificationStepType.DOC_NFC:
                return "take-selfie"
            case VerificationStepType.SELFIE:
            case VerificationStepType.LIVENESS:
                return "information"
            case VerificationStepType.INFORMATION:
            case VerificationStepType.IDV_VERIFICATION:
                return "complete"
            default:
                if (isApp) {
                    return "document"
                }
                return "start"
        }
    }

    const processingStepEvaluationComponent = (
        <ProcessingComponent loadingAnimation="processingId" successAnimation="processingId" success={false}/>
    )

    return {
        evaluateUrl,
        redirectToStep,
        loading,
        processingStepEvaluationComponent
    }
}